import axiosInstance from './axios.service';

const reservaService = {
  // Get all reservas for a specific rifa
  getReservas: async rifaId => {
    const { data } = await axiosInstance.get(`/api/reservas/${rifaId}`);
    return data;
  },

  // Get all reservas for the logged-in user (by comprador email)
  getReservasForUser: async email => {
    const { data } = await axiosInstance.get(`/api/reservas/forUser/${encodeURIComponent(email)}`);
    return data;
  },

  // Get reservas by status with pagination
  // Optional cuenta filter: account id, or 'none' for reservations without account
  getReservasByStatus: async (rifaId, status, skip, limit, cuenta) => {
    const query = cuenta ? `?cuenta=${encodeURIComponent(cuenta)}` : '';
    const { data } = await axiosInstance.get(
      `/api/reservas/byStatus/${rifaId}/${status}/${skip}/${limit}${query}`
    );
    return data;
  },

  // Get non-pending reservas with pagination
  getNoPendientes: async (rifaId, skip, limit) => {
    const { data } = await axiosInstance.get(
      `/api/reservas/noPendientes/${rifaId}/${skip}/${limit}`
    );
    return data;
  },

  // Create a new reserva
  createReserva: async reservaData => {
    const { data } = await axiosInstance.post('/api/reservas/', reservaData);
    return data;
  },

  // Confirm a reserva
  confirmarReserva: async (id, confirmData) => {
    const { data } = await axiosInstance.put(`/api/reservas/confirmarReserva/${id}`, confirmData);
    return data;
  },

  submitPaymentProof: async (id, data) => {
    const response = await axiosInstance.put(`/api/reservas/submit-proof/${id}`, data);
    return response.data;
  },

  // Verify a reserva
  verificarReserva: async (id, responsableVerificacion) => {
    const { data } = await axiosInstance.put(`/api/reservas/verificarReserva/${id}`, {
      responsableVerificacion,
    });
    return data;
  },

  // Set reserva to automation state
  automatizarReserva: async id => {
    const { data } = await axiosInstance.put(`/api/reservas/automatizarReserva/${id}`);
    return data;
  },

  // Cancel a reserva
  cancelarReserva: async (id, cancelData) => {
    const { data } = await axiosInstance.put(`/api/reservas/cancelarReserva/${id}`, cancelData);
    return data;
  },

  // Delete a reserva
  deleteReserva: async id => {
    const { data } = await axiosInstance.delete(`/api/reservas/${id}`);
    return data;
  },

  // Get a single reserva by ID
  getReserva: async id => {
    const { data } = await axiosInstance.get(`/api/reservas/${id}`);
    return data;
  },

  // Get reserva with tickets
  getReservaConTickets: async id => {
    const { data } = await axiosInstance.get(`/api/reservas/detail/${id}`);
    return data;
  },

  // Admin: same as above but always with the numbers (the public one hides them when the bono does)
  getReservaConTicketsAdmin: async id => {
    const { data } = await axiosInstance.get(`/api/reservas/admin/detail/${id}`);
    return data;
  },

  // Update a reserva
  updateReserva: async (id, reservaData) => {
    const { data } = await axiosInstance.put(`/api/reservas/${id}`, reservaData);
    return data;
  },

  // Download tickets as ZIP
  descargarBoletoReservaZip: async id => {
    try {
      const response = await axiosInstance.put(
        `/api/reservas/descargarBoletoReservaZip/${id}`,
        {},
        {
          responseType: 'arraybuffer',
          timeout: 180000, // PDF batch can take a while (Puppeteer + multiple tickets)
          maxContentLength: Infinity,
          headers: {
            Accept: 'application/zip, application/json',
          },
        }
      );

      // Check if the response is JSON (error message)
      const contentType = response.headers['content-type'];
      if (contentType && contentType.includes('application/json')) {
        const decoder = new TextDecoder('utf-8');
        const errorMessage = JSON.parse(decoder.decode(response.data));
        throw new Error(errorMessage.message || 'Error al descargar los boletos');
      }

      return response.data;
    } catch (error) {
      if (error.response?.data instanceof ArrayBuffer) {
        const decoder = new TextDecoder('utf-8');
        const errorMessage = JSON.parse(decoder.decode(error.response.data));
        throw new Error(errorMessage.message || 'Error al descargar los boletos');
      }
      throw error;
    }
  },

  // Download single ticket
  descargarBoletoReserva: async id => {
    const { data } = await axiosInstance.put(
      `/api/reservas/descargarBoletoReserva/${id}`,
      {},
      {
        responseType: 'arraybuffer',
        timeout: 120000,
        headers: {
          Accept: 'application/pdf',
        },
      }
    );
    return data;
  },

  // Upload comprobante
  uploadComprobante: async formData => {
    const { data } = await axiosInstance.post('/api/reservas/uploadComprobante/', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return data;
  },

  // Export reservas to Excel
  exportToExcel: async rifaId => {
    const { data } = await axiosInstance.get(`/api/reservas/exportExcel/${rifaId}`, {
      responseType: 'blob',
    });
    return data;
  },
};

export default reservaService;
